import { Component, OnInit, AfterViewInit, OnDestroy, HostBinding, HostListener } from '@angular/core';
import { Location } from '@angular/common';
import { Router, NavigationEnd, NavigationStart, ActivatedRoute } from '@angular/router';
import { startWith, delay, tap, filter, map } from 'rxjs/operators';
import { IBlankslateOptions } from '@mono-repo/ui';
import { LoadingIndicatorService } from '@mono-repo/loader';
import { IMessage, MessageService } from '@mono-repo/messages';
import { environment } from '../environments_mc/environment';
import { TranslateService, UtilityService } from '@mono-repo/util';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { ConfigService } from './config.service';
import { DeeplinkService } from './cockpit/_shared/services/deeplink.service';
import { OauthService } from '@mono-repo/oauth';
import { SystemEnvironment } from '@mono-repo/data';
import { TrackingService } from './tracking.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {
  @HostBinding('class') class = 'dt-l-app';

  public messages: IMessage[];
  public pageClass: string;
  public loading = false;
  public loadingAnnouncement = '';
  public blankslateOptions: IBlankslateOptions;
  private findAndFocusTitle: any;
  private findAndFocusPreviousElementThatTriggeredNavigation: any;
  private routerSubscription: Subscription;
  private loaderSubscription: Subscription;
  private loadingAnnouncementTimer: ReturnType<typeof setTimeout> | null = null;
  private visitedRoutes: { [key: string]: string | null } = {}; // This object stores the visitedRoutes as a key-value pair of: url as key, and the document.activeElement that activated the next route as value

  constructor(
    private readonly location: Location,
    private readonly router: Router,
    private readonly loaderService: LoadingIndicatorService,
    private readonly messageService: MessageService,
    private readonly translateService: TranslateService,
    private readonly configService: ConfigService,
    private readonly utilityService: UtilityService,
    private readonly title: Title,
    private readonly deeplinkService: DeeplinkService,
    private readonly oauthService: OauthService,
    private readonly trackingService: TrackingService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    this.messages = [];
    // The consent wizard is lazy-loaded, so we use a dynamic import here to keep it out of the main bundle.
    // The URL is passed after APP_INITIALIZER has completed loadConfig().
    import('@mono-repo/wc-consentmanagement').then((module) => {
      module.overrideConsentmanagementTranslations({
        privacyPolicyUrl: this.configService.getConfig().privacyPolicyUrl,
      });
    });
    if (environment.analyticsTool?.tool) {
      this.trackingService.init();
    }
    if (!window['device']) {
      (window['device'] as any) = { platform: 'browser' };
    }
  }

  @HostListener('window:click', ['$event'])
  @HostListener('window:touch', ['$event'])
  catchHref(event: Event) {
    let target = event.target as HTMLElement;
    while (target) {
      if (target instanceof HTMLAnchorElement) {
        const link = target.getAttribute('href');
        if (link) {
          if (link.includes('http')) {
            this.utilityService.openExternalLink(link);
          }
          if (link.includes('tel:')) {
            this.utilityService.openPhoneLink(link.slice(link.indexOf(':') + 1));
          }
          if (link.includes('mailto:')) {
            this.utilityService.openMailLink(link.slice(link.indexOf(':') + 1));
          }
          if (link.includes('internal:')) {
            this.router.navigateByUrl(link.slice(link.indexOf(':') + 1));
          }
          event.preventDefault();
        }
        break;
      }
      target = target.parentElement;
    }
  }

  @HostListener('window:keydown', ['$event'])
  onKeyPress(event: KeyboardEvent): void {
    if (event.keyCode === 13 && (event.target as any).outerHTML.indexOf('<input') > -1) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  ngOnInit() {
    this.translateService.setCurrentLanguageByOdataId('D');
    this.routerSubscription = this.router.events.subscribe((event: NavigationStart | NavigationEnd) => {
      if (event instanceof NavigationStart) {
        this.visitedRoutes[this.router.url] = document.activeElement.getAttribute('data-cy') || null; // @TODO: Make sure that there are no instances where there is no document.activeElement // Records the activeElement that triggered the navigation, so we can focus this later in case of a navback (this.router.url is the "old" url on NavigationStart, and the "new" one on NavigationEnd)
        // Finds and focuses on the title for accessibility
        this.findAndFocusTitle = function () {
          setTimeout(() => {
            // don't continue if loading bar still up
            if (document.getElementsByClassName('dt-c-loader-bar')[0]) {
              this.findAndFocusTitle();
              return;
            }

            if (event.url === '/cockpit/faq') {
              // The Faq page doesn't have a loader indicator, so the page doesn't wait for it to load to seek its header. That is why we need to manually check if the router.url is /faq.
              setTimeout(() => {
                (document.getElementById('feedTitle') as HTMLElement).focus();
              }, 300); // There is an extra timeout here because /faq doesn't have a loading indicator
              // in LPA case we need to init focus header of LPA
            } else if (document.querySelectorAll('.dt-c-low-permission-access__header__title')[0] as HTMLElement) {
              (document.querySelectorAll('.dt-c-low-permission-access__header__title')[0] as HTMLElement).focus();
            } else if (
              document.querySelectorAll('.dt-l-article__header__title')[
                document.querySelectorAll('.dt-l-article__header__title').length - 1
              ] as HTMLElement
            ) {
              (
                document.querySelectorAll('.dt-l-article__header__title')[
                  document.querySelectorAll('.dt-l-article__header__title').length - 1
                ] as HTMLElement
              ).focus();
            } else if (document.querySelectorAll('.dt-l-article__section__title')[0]) {
              (document.querySelectorAll('.dt-l-article__section__title')[0] as HTMLElement).focus();
            } else {
              this.findAndFocusTitle();
            }
          }, 300);
        };

        // If the navigation is the result of a browser nav-back or nav-forward event
        if (event.navigationTrigger === 'popstate') {
          // restore focus here
          this.findAndFocusPreviousElementThatTriggeredNavigation = function () {
            if (this.visitedRoutes[event.url]) {
              // If the element-to-focus string is not empty, carry on
              const elementToFocus = document.querySelector(
                `[data-cy="${this.visitedRoutes[event.url]}"]`,
              ) as HTMLElement;
              if (elementToFocus) {
                elementToFocus.focus();
              } else {
                setTimeout(() => {
                  this.findAndFocusPreviousElementThatTriggeredNavigation();
                }, 300);
              }
            } else {
              // if route was not loaded previously, focus on title
              this.findAndFocusTitle();
            }
          };
          this.findAndFocusPreviousElementThatTriggeredNavigation();
        } else {
          //focus title on page title
          this.findAndFocusTitle();
        }
      }

      if (event instanceof NavigationEnd) {
        // set default class for app and add current path
        this.pageClass = `dt-app page-${event.urlAfterRedirects.substring(1).split('/').join('-')}`;
        this.messageService.hide();
      }
    });

    this.setPageTitle();

    if (environment.systemEnvironment === SystemEnvironment.APP) {
      this.deeplinkService.init();

      // Check if a valid refresh_token exists. If it does, it means that the user is "logged in". We then reroute them to the cockpit.
      if (this.oauthService.checkIfRefreshTokenIsValid()) {
        // If user is "logged in" and unlock functionality is enabled, lock the screen at startup.
        if (this.configService.getConfig().clientSessionTime != 0) {
          localStorage.removeItem('isUnlocked');
          this.router.parseUrl('/unlock');
        } else {
          // If user is logged in and unlocked, launch the app straight in cockpit.
          this.router.navigateByUrl('/cockpit/start');
        }
      }
    }
  }

  ngAfterViewInit() {
    this.loaderSubscription = this.loaderService.loaderState
      .pipe(
        startWith(null),
        delay(0),
        tap((response) => {
          if (response) {
            this.loading = response.isLoading;

            if (response.isLoading) {
              this.loadingAnnouncementTimer = setTimeout(() => {
                this.loadingAnnouncement = this.translateService.translate('DATA_LOADING');
              }, 300);
            } else {
              if (this.loadingAnnouncementTimer) {
                clearTimeout(this.loadingAnnouncementTimer);
                this.loadingAnnouncementTimer = null;
              }
              this.loadingAnnouncement = '';
            }
          }
        }),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.routerSubscription.unsubscribe();
    this.loaderSubscription.unsubscribe();
  }

  private setPageTitle(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;
          if (!child) {
            return this.activatedRoute.snapshot.data.title;
          }
          while (child.firstChild) {
            child = child.firstChild;
          }
          if (child.snapshot.data.title) {
            return child.snapshot.data.title;
          }
        }),
      )
      .subscribe(() => {
        let hash = this.router.url.split('/').join('_');
        if (hash.indexOf('?') > -1) {
          hash = hash.split('?')[0];
        }
        if (hash.indexOf(':') > -1) {
          hash = hash.split(':')[0];
        }

        if (!isNaN(Number(hash.split('_')[hash.split('_').length - 1]))) {
          const work = hash.split('_');
          work.pop();
          hash = `${work.join('_')}_details`;
        }

        if (hash.indexOf('consentmanagement') > -1) {
          hash = hash.slice(0, hash.indexOf('consentmanagement') + 17);
        }

        if (hash.indexOf('publicSurvey') > -1) {
          const newHash = hash.split('_');
          newHash.pop();
          hash = newHash.join('_');
        }

        if (hash.indexOf('lpa') > -1) {
          const newHash = hash.split('_');
          newHash.splice(2, 1);
          hash = newHash.join('_');
        }

        if (hash.indexOf('setpassword') > -1) {
          hash = '_setpassword';
        }

        const prefix = this.translateService.translate(`PAGETITLE_PREFIX`);
        const title = this.translateService.translate(`PAGETITLE${hash}`);
        const finalTitle = `${prefix}${title}`;

        if (finalTitle.indexOf('PAGETITLE_') > -1) {
          // console.warn(`trying to set unknown page title: ${finalTitle}` );
          return;
        }
        if (finalTitle !== this.title.getTitle()) {
          this.title.setTitle(finalTitle);
        }
      });
  }

  public browseBack(): void {
    this.location.back();
  }
}
